import React, { useState } from 'react';
import { Language } from '../types/kundali';
import { AdvancedLifeAnalysisResult } from '../types/advancedLifeAnalysis';
import { ChevronDown, ChevronUp, Database, Table, Calculator, Sparkles, CheckCircle2 } from 'lucide-react';

interface ForensicCalculationAccordionProps {
  analysisResult: AdvancedLifeAnalysisResult;
  lang: Language;
  className?: string;
}

export const ForensicCalculationAccordion: React.FC<ForensicCalculationAccordionProps> = ({
  analysisResult,
  lang,
  className = ''
}) => {
  const isHi = lang === 'hi';
  const [isOpen, setIsOpen] = useState(false);

  const { shadbalaSummary, ashtakavargaSummary, bhavaAssessments, dashaTimeline } = analysisResult;

  return (
    <div
      id="forensic-calculation-accordion"
      className={`bg-[#FDFCF9] dark:bg-[#1E2522] border border-[#E2DED5] dark:border-[#3B433F] rounded-2xl shadow-xs overflow-hidden transition-all ${className}`}
    >
      {/* Header Toggle Button (Collapsed by Default) */}
      <button
        id="btn-toggle-forensic-calculations"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 sm:p-5 flex items-center justify-between bg-astro-card dark:bg-[#202724] hover:bg-[#F0EBE1] dark:hover:bg-[#252D2A] transition-colors text-left min-h-[48px] touch-manipulation cursor-pointer"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2.5">
          <Calculator className="w-5 h-5 text-astro-secondary dark:text-[#86A7A0] flex-shrink-0" />
          <div>
            <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-[#263633] dark:text-[#F2F1EB]">
              {isHi ? 'विस्तृत गणितीय एवं ज्योतिर्गणितीय सांख्यिकी' : 'Forensic Astrological Calculation Data'}
            </h4>
            <p className="text-[11px] text-[#52635F] dark:text-[#B9C0BB]">
              {isHi ? 'षडबल अनुपात, सर्वाष्टकवर्ग, भाव स्थिति एवं पूर्ण दशा क्रम (विस्तार हेतु क्लिक करें)' : 'Shadbala components, SAV points, Bhava lord degrees & timeline raw metrics (Click to expand)'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs font-semibold text-[#52635F] dark:text-[#B9C0BB] hidden sm:inline">
            {isOpen ? (isHi ? 'संक्षिप्त करें' : 'Collapse') : (isHi ? 'विस्तार करें' : 'Expand')}
          </span>
          {isOpen ? <ChevronUp className="w-4 h-4 text-[#52635F] dark:text-[#B9C0BB]" /> : <ChevronDown className="w-4 h-4 text-[#52635F] dark:text-[#B9C0BB]" />}
        </div>
      </button>

      {/* Expanded Content Tables */}
      {isOpen && (
        <div className="p-4 sm:p-6 border-t border-[#E2DED5] dark:border-[#3B433F] space-y-6 animate-fadeIn bg-[#FFFEFB] dark:bg-[#1E2522]">
          {/* 1. Shadbala Summary Table */}
          <div className="space-y-2">
            <h5 className="text-xs font-bold uppercase tracking-wider text-[#263633] dark:text-[#F2F1EB] flex items-center gap-1.5">
              <Table className="w-3.5 h-3.5 text-astro-secondary dark:text-[#86A7A0]" />
              <span>{isHi ? '१. षडबल ग्रह सामर्थ्य तालिका (रूप एवं अनुपात)' : '1. Shadbala Planetary Strength Matrix'}</span>
            </h5>
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left border border-[#E2DED5] dark:border-[#3B433F] rounded-lg overflow-hidden">
                <thead className="bg-astro-card dark:bg-[#202724] text-[#263633] dark:text-[#F2F1EB] font-bold text-[11px]">
                  <tr>
                    <th className="p-2.5">Planet</th>
                    <th className="p-2.5">Shadbala (Rupas)</th>
                    <th className="p-2.5">Strength Ratio</th>
                    <th className="p-2.5">Rank</th>
                    <th className="p-2.5">Classical Dignity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2DED5] dark:divide-[#3B433F]">
                  {shadbalaSummary.map((s) => (
                    <tr key={s.planet} className="hover:bg-astro-card/50 dark:hover:bg-[#252D2A]/30">
                      <td className="p-2.5 font-bold text-[#263633] dark:text-[#F2F1EB]">{s.planet}</td>
                      <td className="p-2.5 font-mono">{s.rupas.toFixed(2)}</td>
                      <td className="p-2.5 font-mono">
                        <span className={`font-bold ${s.ratio >= 1.0 ? 'text-[#3D7A68] dark:text-[#86A7A0]' : 'text-astro-accent dark:text-[#D4A373]'}`}>
                          {s.ratio.toFixed(2)}x
                        </span>
                      </td>
                      <td className="p-2.5 font-mono">#{s.rank}</td>
                      <td className="p-2.5 text-[#52635F] dark:text-[#B9C0BB]">{s.dignity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 2. Ashtakavarga SAV Points Table */}
          <div className="space-y-2">
            <h5 className="text-xs font-bold uppercase tracking-wider text-[#263633] dark:text-[#F2F1EB] flex items-center gap-1.5">
              <Table className="w-3.5 h-3.5 text-astro-secondary dark:text-[#86A7A0]" />
              <span>{isHi ? '२. सर्वाष्टकवर्ग बिंदु एवं भाव सामर्थ्य' : '2. Sarvashtakavarga (SAV) House Points'}</span>
            </h5>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
              {ashtakavargaSummary.map((av) => (
                <div
                  key={av.house}
                  className={`p-2 rounded-lg border text-center text-xs ${
                    av.savPoints >= 28
                      ? 'bg-astro-teal-tint/80 dark:bg-[#1A3331]/30 border-astro-secondary/30 dark:border-[#39706C]/40'
                      : 'bg-astro-card dark:bg-[#202724]/40 border-[#E2DED5] dark:border-[#3B433F]'
                  }`}
                >
                  <div className="font-bold text-[#263633] dark:text-[#F2F1EB]">House {av.house}</div>
                  <div className="text-[10px] text-[#52635F] dark:text-[#B9C0BB]">{av.sign}</div>
                  <div className={`text-sm font-bold font-mono my-0.5 ${av.savPoints >= 28 ? 'text-astro-secondary dark:text-[#86A7A0]' : 'text-astro-accent dark:text-[#D4A373]'}`}>
                    {av.savPoints} pts
                  </div>
                  <div className="text-[9px] text-[#52635F] dark:text-[#B9C0BB] uppercase">{av.evaluation}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 3. Upcoming Dasha Sequences */}
          <div className="space-y-2">
            <h5 className="text-xs font-bold uppercase tracking-wider text-[#263633] dark:text-[#F2F1EB] flex items-center gap-1.5">
              <Table className="w-3.5 h-3.5 text-astro-secondary dark:text-[#86A7A0]" />
              <span>{isHi ? '३. आगामी विंशोत्तरी दशा व अंतरदशा क्रम' : '3. Vimshottari Dasha Sequences'}</span>
            </h5>
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left border border-[#E2DED5] dark:border-[#3B433F] rounded-lg overflow-hidden">
                <thead className="bg-astro-card dark:bg-[#202724] text-[#263633] dark:text-[#F2F1EB] font-bold text-[11px]">
                  <tr>
                    <th className="p-2.5">Mahadasha</th>
                    <th className="p-2.5">Antardasha</th>
                    <th className="p-2.5">Start Date</th>
                    <th className="p-2.5">End Date</th>
                    <th className="p-2.5">House Involvement</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2DED5] dark:divide-[#3B433F]">
                  <tr className="bg-astro-teal-tint/60 dark:bg-[#1A3331]/30 font-semibold">
                    <td className="p-2.5 text-astro-secondary dark:text-[#86A7A0]">{dashaTimeline.activeMD} (Current)</td>
                    <td className="p-2.5 text-astro-secondary dark:text-[#86A7A0]">{dashaTimeline.activeAD}</td>
                    <td className="p-2.5 font-mono">{dashaTimeline.startDate}</td>
                    <td className="p-2.5 font-mono">{dashaTimeline.endDate}</td>
                    <td className="p-2.5 font-mono">Current Period</td>
                  </tr>
                  {dashaTimeline.upcomingRelevantPeriods.map((up, i) => (
                    <tr key={i} className="hover:bg-astro-card/50 dark:hover:bg-[#252D2A]/30">
                      <td className="p-2.5 font-bold text-[#263633] dark:text-[#F2F1EB]">{up.md}</td>
                      <td className="p-2.5 font-medium">{up.ad}</td>
                      <td className="p-2.5 font-mono text-[#52635F] dark:text-[#B9C0BB]">{up.startDate}</td>
                      <td className="p-2.5 font-mono text-[#52635F] dark:text-[#B9C0BB]">{up.endDate}</td>
                      <td className="p-2.5 font-mono text-[#52635F] dark:text-[#B9C0BB]">Houses {up.activatedHouses.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

